import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TextField, InputAdornment, IconButton, Button, Tooltip } from "@mui/material";
import Accordion from 'react-bootstrap/Accordion';
import SearchIcon from '@mui/icons-material/Search';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import moment from "moment";
import { URLS } from "../../helpers/URLS";
import useAxios from "../../helpers/Hooks/useAxios";
import SkeletonLoader from "../../loaders/Skeleton";
import NoData from "../../loaders/NoData";
import PaginationGlobal from "../TablePagination.js/PaginationGlobal";
import Description from "../Common/Description";
import { matchesSearch } from "../../helpers/search";
import { downloadLink } from "../../helpers/constFIles";
import { encrypt } from "../../helpers/utilities";
import "../../assets/scss/tables.scss";
import AISummaryModal from "./AISummaryModal";

/* the fields the search box looks through */
const SEARCH_FIELDS = [
    'employeeCode', 'employeeName', 'designation',
    'emailID', 'mobile', 'managerName',
];

const FILTERS = [
    { key: 'all', label: 'Everyone' },
    { key: 'reported', label: 'Reported' },
    { key: 'missing', label: 'No reports' },
];

const initials = (name) => {
    if (!name) return "?";
    const parts = String(name).trim().split(/\s+/);
    return ((parts[0] ? parts[0][0] : "") +
        (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
};

const plural = (count, word) => `${count} ${count === 1 ? word : `${word}s`}`;

/* One person's days, in the same shape the My Work Reports page uses, so a
   day reads identically whichever tab you opened it from. */
const EmployeeDays = ({ employee }) => {

    const navigate = useNavigate();

    if (!employee.days.length) {
        return (
            <p className="wh-section-empty wh-report-none">
                <EventBusyOutlinedIcon />
                {employee.employeeName} has not submitted any work report in this range.
            </p>
        );
    }

    return (
        <>
            <div className="wh-day-actions">
                <Button variant="outlined" size="small" startIcon={<OpenInNewRoundedIcon />}
                    onClick={() => navigate(
                        `/report-view/${encrypt("employeeCode")}/${encrypt(employee.employeeCode)}`,
                        { state: { title: employee.employeeName } })}>
                    Open full report
                </Button>
            </div>

            <Accordion defaultActiveKey="0" className="wh-accordion wh-accordion-nested">
                {employee.days.map((day, index) => (
                    <Accordion.Item key={day.date} eventKey={String(index)}>
                        <Accordion.Header>
                            <span className="wh-day-date">
                                <CalendarMonthOutlinedIcon />
                                {moment(day.date).format('DD MMMM YYYY')}
                            </span>
                            <span className="wh-day-meta">
                                <span className="wh-pill">{day.data.length} {day.data.length === 1 ? 'entry' : 'entries'}</span>
                                <span className="wh-pill wh-pill-good">{plural(day.hours, 'hour')}</span>
                            </span>
                        </Accordion.Header>
                        <Accordion.Body>
                            <div className="table-responsive wh-table-shell">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th scope="col" className="wh-col-shrink">#</th>
                                            <th scope="col">Project Name</th>
                                            <th scope="col">Activity Name</th>
                                            <th scope="col" className="wh-col-shrink">Hours</th>
                                            <th scope="col">Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {day.data.map((entry, subIndex) => (
                                            <tr key={entry._id || subIndex}>
                                                <td className="wh-col-shrink">{subIndex + 1}</td>
                                                <td>{(entry.project && entry.project.projectName) || "--"}</td>
                                                <td>{(entry.Activity && entry.Activity.activityName) || "--"}</td>
                                                <td className="wh-col-shrink text-nowrap">{entry.hours}</td>
                                                <td className="wh-cell-muted">
                                                    <Description text={entry.description} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                ))}
            </Accordion>
        </>
    );
};

const WorkReport = () => {

    const { fetchData } = useAxios();

    const [loadSkeleton, setLoadSkeleton] = useState(true);
    const [openAISummary, setOpenAISummary] = useState(false);

    const [search, setSearch] = useState('');

    const [filter, setFilter] = useState('all');

    const [pageNumber, setPageNumber] = useState(1);

    const [rowsPerPage, setRowsPerPage] = useState(15);

    const [searchParameters, setSearchParameters] = useState({
        startDate: new Date(moment().subtract(10, 'days').format('YYYY-MM-DD')),
        endDate: new Date()
    });

    /* one row per team member, each carrying their days for the range */
    const [team, setTeam] = useState([]);

    const getTeamReports = async () => {
        setLoadSkeleton(true);
        const start = searchParameters.startDate && moment(searchParameters.startDate).isValid()
            ? moment(searchParameters.startDate).format('YYYY-MM-DD') : '';
        const end = searchParameters.endDate && moment(searchParameters.endDate).isValid()
            ? moment(searchParameters.endDate).format('YYYY-MM-DD') : '';

        const resp = await fetchData({
            url: `${URLS.getTeamDailyStatus}?startDate=${start}&endDate=${end}`,
            method: 'get'
        });

        setTeam(resp.status === 200 && Array.isArray(resp.data) ? resp.data : []);
        setLoadSkeleton(false);
    };

    useEffect(() => {
        getTeamReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParameters.startDate, searchParameters.endDate]);

    const counts = useMemo(() => ({
        all: team.length,
        reported: team.filter(member => member.daysReported > 0).length,
        missing: team.filter(member => !member.daysReported).length,
    }), [team]);

    const totalHours = useMemo(() =>
        team.reduce((sum, member) => sum + Number(member.totalHours || 0), 0), [team]);

    const result = useMemo(() => team
        .filter(member => filter === 'all'
            || (filter === 'reported' ? member.daysReported > 0 : !member.daysReported))
        .filter(member => matchesSearch(member, search, SEARCH_FIELDS)),
        [team, filter, search]);

    /* a narrower list can leave you past the last page */
    useEffect(() => { setPageNumber(1); }, [search, filter, team]);

    const pageRows = result.slice((pageNumber - 1) * rowsPerPage, pageNumber * rowsPerPage);

    return (
        <div className="wh-table">
            <div className="wh-toolbar">
                <h4 className="title">Team Work Reports</h4>

                <div className="wh-toolbar-controls wh-toolbar-controls--end">
                    <TextField
                        placeholder="Search name, code, email, manager…"
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="wh-search-field"
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                            endAdornment: search
                                ? <InputAdornment position="end">
                                    <IconButton size="small" className="wh-search-clear"
                                        aria-label="Clear search" onClick={() => setSearch('')}>
                                        <CloseRoundedIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                                : null,
                        }}
                    />
                </div>
            </div>

            <div className="wh-filter-bar">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                        label="Start Date"
                        inputFormat="DD/MM/YYYY"
                        value={searchParameters.startDate}
                        maxDate={new Date()}
                        onChange={(date) => {
                            const validDate = date && date.toDate ? date.toDate() : date;
                            setSearchParameters(prev => ({ ...prev, startDate: validDate }));
                        }}
                        renderInput={(params) => <TextField {...params} size="small" required />}
                    />
                </LocalizationProvider>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                        label="End Date"
                        inputFormat="DD/MM/YYYY"
                        value={searchParameters.endDate}
                        minDate={searchParameters.startDate}
                        maxDate={new Date()}
                        onChange={(date) => {
                            const validDate = date && date.toDate ? date.toDate() : date;
                            setSearchParameters(prev => ({ ...prev, endDate: validDate }));
                        }}
                        renderInput={(params) => <TextField {...params} size="small" required />}
                    />
                </LocalizationProvider>

                <Button variant="contained" startIcon={<SearchRoundedIcon />}
                    onClick={() => getTeamReports()}>Search</Button>

                <Button variant="contained" startIcon={<AutoAwesomeRoundedIcon />}
                    onClick={() => setOpenAISummary(true)}>AI Summary</Button>

                <div className="wh-segmented wh-filter-bar-end" role="group" aria-label="Filter team">
                    {FILTERS.map(option => (
                        <button type="button" key={option.key}
                            className={filter === option.key ? "is-active" : ""}
                            aria-pressed={filter === option.key}
                            onClick={() => setFilter(option.key)}>
                            {option.label}<em>{counts[option.key]}</em>
                        </button>
                    ))}
                </div>
            </div>

            {!loadSkeleton && team.length > 0 && (
                <p className="wh-report-recap">
                    <CalendarMonthOutlinedIcon />
                    {moment(searchParameters.startDate).format('DD MMM YYYY')} – {moment(searchParameters.endDate).format('DD MMM YYYY')}
                    <span>·</span>
                    {counts.reported} of {counts.all} reported
                    <span>·</span>
                    {plural(Number(totalHours.toFixed(2)), 'hour')} logged
                </p>
            )}

            {loadSkeleton
                ? <SkeletonLoader />
                : (result.length > 0
                    ? <>
                        <Accordion className="wh-accordion wh-accordion-people">
                            {pageRows.map((member, index) => (
                                <Accordion.Item key={member.employeeCode} eventKey={String(index)}>
                                    <Accordion.Header>
                                        <span className="wh-person">
                                            {member.profilePic
                                                ? <img className="wh-avatar wh-avatar-sm"
                                                    src={downloadLink + member.profilePic} alt=""
                                                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                                : null}
                                            <span className="wh-avatar wh-avatar-sm">{initials(member.employeeName)}</span>
                                            <span className="wh-person-stack">
                                                <b>{member.employeeName}</b>
                                                <em>{member.designation || member.employeeCode}</em>
                                            </span>
                                        </span>

                                        <span className="wh-day-meta">
                                            {member.daysReported > 0
                                                ? <>
                                                    <Tooltip title="Most recent report" placement="top" arrow>
                                                        <span className="wh-pill wh-pill-quiet">
                                                            <CalendarMonthOutlinedIcon />
                                                            {moment(member.lastReportedOn).format('DD MMM YYYY')}
                                                        </span>
                                                    </Tooltip>
                                                    <span className="wh-pill">{plural(member.daysReported, 'day')}</span>
                                                    <span className="wh-pill wh-pill-good">{plural(member.totalHours, 'hour')}</span>
                                                </>
                                                : <span className="wh-pill wh-pill-warn">No reports</span>}
                                        </span>
                                    </Accordion.Header>

                                    <Accordion.Body>
                                        <EmployeeDays employee={member} />
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>

                        <PaginationGlobal
                            count={Math.ceil(result.length / rowsPerPage)}
                            pageNumber={pageNumber} func={setPageNumber}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(size) => { setRowsPerPage(size); setPageNumber(1); }} />
                    </>
                    : <NoData />)
            }

            {openAISummary && (
                <AISummaryModal
                    open={openAISummary}
                    onClose={() => setOpenAISummary(false)}
                    searchParameters={searchParameters}
                />
            )}
        </div>
    );
}

export default WorkReport;
